import { Modules } from '../../../dtos/Modules';

// Match backend DeepLinkBuilder:
//   Grant:   /grantdetails/{grantId}
//   Module:  /grantdetails/{grantId}#{module}
//   Task:    /grantdetails/{grantId}#{module}-task-{phaseId}
//   Subtask: /grantdetails/{grantId}#{module}-task-{phaseId}-subtask-{stepId}

// Extract IDs from -task-{id} and optional -subtask-{id} segments
const DEEP_LINK_REGEX = /#[a-z]+-task-([a-z0-9-]+?)(?:-subtask-([a-z0-9-]+))?$/i;

export interface DeepLinkParsed {
    taskId: string | null;
    subtaskId: string | null;
}

export function parseDeepLinkHash(hash: string | null): DeepLinkParsed {
    if (!hash) return { taskId: null, subtaskId: null };

    const match = hash.match(DEEP_LINK_REGEX);
    if (match) {
        return {
            taskId: match[1] || null,
            subtaskId: match[2] || null
        };
    }

    return { taskId: null, subtaskId: null };
}

// Builds the hash half of a task/subtask link, mirroring DeepLinkBuilder.Task/.Subtask. Kept next to
// the parser above so the two sides of the format stay together (see the round-trip test).
// Returns null when the module or task is unknown - e.g. a grant-level note has no phase.
export function buildDeepLinkHash(
    moduleTypeId: number | null | undefined,
    taskId: string | null | undefined,
    subtaskId?: string | null,
): string | null {
    const module = moduleTypeId != null ? Modules[moduleTypeId] : undefined;
    if (!module || !taskId) return null;

    const base = `#${module.toLowerCase()}-task-${taskId}`;
    return subtaskId ? `${base}-subtask-${subtaskId}` : base;
}

// Full in-app route to a task/subtask, for callers navigating from outside the grant (the dashboard).
// Callers already on the grant page set the hash instead - see GrantDetailPhases.
export function buildDeepLinkRoute(
    grantId: string,
    moduleTypeId: number | null | undefined,
    taskId: string | null | undefined,
    subtaskId?: string | null,
): string | null {
    const hash = buildDeepLinkHash(moduleTypeId, taskId, subtaskId);
    return hash ? `/grantdetails/${grantId}${hash}` : null;
}
